import {
  Application,
  type DisplayObject,
  Graphics,
  FederatedPointerEvent,
} from 'pixi.js';
import type { HitCircle } from '@musicgame/chart';
import { preemptMs } from '@musicgame/chart';
import Theme from './themes/Theme';
import { GameClock, judge, window50, sliderWindow, getInputOffsetMs } from './timing';
import { circleRadius, followRadius } from './themes/circle/radius';
import { applyAR } from './mods.js';
import { InputManager, ReplayFrame } from './lib/utils';
import type { PathTable } from './utils/pathLinker';
import { TOKENS } from './tokens';
import Cursor from './renderers/Cursor';
import ReplayCursor from './renderers/ReplayCursor';
import type { StageLayerSet } from './StageLayers';
import {
  coalesceSliderChains,
  isSlider,
  assertNoConsecutiveSliders,
  endOf,
  startOf,
} from './utils/coalesceSliderChains';
import { POST_GAP_MS } from './logic/directedChart';

interface SliderState {
  note: any;
  obj: any;
  preview?: DisplayObject;
  table: PathTable;
  followRadius: number;
  events: { time: number; type: string }[];
  tEnd: number;
  lastHold: boolean;
  startHit: boolean;
  lastHoldTime: number;
}

interface SpinnerState {
  note: any;
  obj: any;
  total: number;
  lastAngle: number | null;
  required: number;
  bonus: number;
}

/**
 * Main gameplay scene. Responsible for scheduling notes using the global
 * {@link GameClock} so that they travel a fixed time before reaching their
 * receptors.
 */
export default class MainScene {
  private travelTime: number;
  private activeCircles: { note: HitCircle; obj: DisplayObject }[] = [];
  private activeSliders: SliderState[] = [];
  private activeSpinners: SpinnerState[] = [];
  private hoveredCircles = new Set<DisplayObject>();
  private input: InputManager;
  private recording: ReplayFrame[] = [];
  private recordActive = true;
  private nextRecordSec = 0;
  private recordTicker = () => {
    if (!this.recordActive) return;
    const t = GameClock.now() - getInputOffsetMs();
    this.recording.push({ t, x: this.input.x, y: this.input.y, buttons: this.input.buttons });
    this.nextRecordSec += 1 / 240;
    GameClock.schedule(this.nextRecordSec, this.recordTicker);
  };
  private cs: number;
  private cursor: Cursor;
  private replayCursor?: ReplayCursor;
  private onPointerMove = (e: FederatedPointerEvent): void => {
    this.cursor.position.set(e.globalX, e.globalY);
    const x = e.globalX;
    const y = e.globalY;
    const hitRadius = circleRadius(this.cs) + 5;
    for (const c of this.activeCircles) {
      const obj = c.obj as any;
      const dist = Math.hypot(x - (obj?.x ?? 0), y - (obj?.y ?? 0));
      const hovered = this.hoveredCircles.has(obj);
      if (dist <= hitRadius) {
        if (!hovered && typeof obj.pulse === 'function') {
          obj.pulse();
        }
        this.hoveredCircles.add(obj);
      } else if (hovered) {
        this.hoveredCircles.delete(obj);
      }
    }
  };
  private onPointerDown = (): void => {
    this.cursor.press();
  };

  constructor(
    private app: Application,
    private layers: StageLayerSet,
    private theme: Theme,
    travelTimeSeconds = 1,
    private offsetSec = 0,
    replay = false,
    difficulty: { cs: number } = { cs: 3 },
    private logger?: (msg: string) => void,
  ) {
    this.travelTime = travelTimeSeconds;
    this.cs = difficulty.cs;
    this.input = new InputManager(this.app, !replay);
    // Click/presses trigger hit detection for circles.
    this.input.onHit((e) => {
      const nx = (e.x / this.app.screen.width) * 9;
      const ny = (e.y / this.app.screen.height) * 16;
      this.logger?.(
        `input ${e.button} at (${nx.toFixed(2)}, ${ny.toFixed(2)})`,
      );
      this.handleInput();
    });
    // Holds update slider/spinner tracking.
    this.input.onHold(() => this.handleHold());
    this.nextRecordSec = GameClock.now() / 1000;
    GameClock.schedule(this.nextRecordSec, this.recordTicker);

    this.cursor = new Cursor(this.app);
    this.layers.hud.addChild(this.cursor);
    if (replay) {
      this.replayCursor = new ReplayCursor(this.app);
      this.layers.hud.addChild(this.replayCursor);
    }
    this.app.stage.on('pointermove', this.onPointerMove);
    this.app.stage.on('pointerdown', this.onPointerDown);
  }

  /** Schedule all notes for rendering via the timing utility. */
  loadNotes(notes: any[]): void {
    // Preprocess notes for stacking to slightly offset overlapping objects.
    notes.sort((a, b) => a.tStart - b.tStart);
    const before = notes.filter(isSlider).length;

    // Debug: surface any consecutive sliders before coalescing so we can
    // trace their origin when unexpected chains appear during gameplay.
    const rawSliders = notes.filter(isSlider);
    for (let i = 1; i < rawSliders.length; i++) {
      const prev: any = rawSliders[i - 1];
      const curr: any = rawSliders[i];
      const prevEnd = endOf(prev);
      const currStart = startOf(curr);
      if (!Number.isFinite(prevEnd) || !Number.isFinite(currStart)) {
        const prevIdx = notes.indexOf(prev);
        const currIdx = notes.indexOf(curr);
        this.logger?.(
          `[MainScene] unable to determine slider timing: #${prevIdx} end=${prevEnd}ms, ` +
            `#${currIdx} start=${currStart}ms`,
        );
        continue;
      }
      const gap = currStart - prevEnd;
      if (gap <= POST_GAP_MS) {
        const prevIdx = notes.indexOf(prev);
        const currIdx = notes.indexOf(curr);
        this.logger?.(
          `[MainScene] consecutive slider detected before merge: ` +
            `#${prevIdx} [${startOf(prev)}ms→${prevEnd}ms] -> ` +
            `#${currIdx} [${currStart}ms→${endOf(curr)}ms] (gap ${gap}ms)`,
        );
      }
    }

    notes = coalesceSliderChains(notes, POST_GAP_MS, 10);
    assertNoConsecutiveSliders(notes);
    const after = notes.filter(isSlider).length;
    console.log('sliders before/after merge:', before, after);
    const stackTime = 30; // ms threshold for stacking
    const stackPos = 0.1; // normalized distance threshold
    for (const n of notes) n.stack = 0;
    for (let i = notes.length - 1; i >= 0; i--) {
      let base = notes[i];
      for (let j = i - 1; j >= 0; j--) {
        const prev = notes[j];
        const dt = base.tStart - prev.tStart;
        if (dt > stackTime) break;
        const dx = (base.pos?.x ?? 0) - (prev.pos?.x ?? 0);
        const dy = (base.pos?.y ?? 0) - (prev.pos?.y ?? 0);
        if (Math.hypot(dx, dy) <= stackPos) {
          prev.stack = (base.stack ?? 0) + 1;
          base = prev;
        }
      }
    }

    for (const note of notes) {
      const tStart = note.tStart + this.offsetSec * 1000;
      const ar = applyAR(note.approachRate);
      const preempt = preemptMs(ar);

      GameClock.schedule((tStart - preempt) / 1000, () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const rendered: any = this.theme.renderNote(this.app, note, ar);
        if (!rendered) return;
        let obj = rendered as any;
        let preview: DisplayObject | undefined;
        if (rendered && rendered.preview && rendered.note) {
          preview = rendered.preview as DisplayObject;
          obj = rendered.note;
        }
        if (preview && preview.parent !== this.layers.paths) {
          preview.parent?.removeChild(preview);
          this.layers.paths.addChild(preview);
        }
        if (obj.parent !== this.layers.notes) {
          obj.parent?.removeChild(obj);
          this.layers.notes.addChild(obj);
        }
        const type = isSlider(note)
          ? 'slider'
          : typeof note.tEnd === 'number'
            ? 'spinner'
            : 'circle';
        const nx = note.pos?.x ?? 0;
        const ny = note.pos?.y ?? 0;
        this.logger?.(
          `add ${type} at (${nx.toFixed(2)}, ${ny.toFixed(2)}) t=${note.tStart}`,
        );

        if (isSlider(note)) {
          // Slider
          const state: SliderState = {
            note,
            obj,
            preview,
            table: obj.table,
            followRadius: obj.followRadius ?? followRadius(this.cs),
            events: obj.events ?? [],
            tEnd: obj.tEnd ?? note.tEnd,
            lastHold: false,
            startHit: false,
            lastHoldTime: -Infinity,
          };
          this.activeSliders.push(state);

          // Ensure the slider object disappears when it reaches its end even if
          // there are no explicit events to handle the cleanup (e.g. themes that
          // do not emit an `end` event).
          const tEnd = state.tEnd + this.offsetSec * 1000;
          GameClock.schedule(tEnd / 1000, () => {
            if (!this.activeSliders.includes(state)) return;
            this.fadeOut(state.obj);
            if (state.preview) this.fadeOut(state.preview);
            this.activeSliders = this.activeSliders.filter((s) => s !== state);
          });

          for (const e of state.events) {
            GameClock.schedule((tStart + e.time) / 1000, () => {
              if (!this.activeSliders.includes(state)) return;
              const now = GameClock.now();
              if (e.type === 'start') {
                if (!state.startHit) {
                  const pos = this.toStageCoords(state.note.pos ?? { x: 0, y: 0 });
                  this.spawnHitEffect(pos.x, pos.y, 'miss');
                  const details = {
                    reason: 'slider-start',
                    note: { x: pos.x, y: pos.y, time: tStart },
                    input: { time: now },
                  };
                  this.app.stage.emit('hit', 'miss', details);
                  this.fadeOut(state.obj);
                  if (state.preview) this.fadeOut(state.preview);
                  this.activeSliders = this.activeSliders.filter((s) => s !== state);
                }
                return;
              }
              if (!state.startHit) return;
              const hit = Math.abs(now - state.lastHoldTime) <= sliderWindow();
              if (!hit) {
                const p = state.table.pointAt(e.time);
                this.spawnHitEffect(p.x, p.y, 'miss');
                const details = {
                  reason: 'slider-hold',
                  note: { x: p.x, y: p.y, time: tStart + e.time },
                  input: { x: this.input.x, y: this.input.y, time: now },
                  diff: now - state.lastHoldTime,
                };
                this.app.stage.emit('hit', 'sliderMiss', details);
              } else {
                this.app.stage.emit('hit', e.type);
              }
              if (e.type === 'end') {
                this.fadeOut(state.obj);
                if (state.preview) this.fadeOut(state.preview);
                this.activeSliders = this.activeSliders.filter((s) => s !== state);
              }
            });
          }
        } else if (typeof note.tEnd === 'number') {
          // Spinner
          const duration = note.tEnd - note.tStart;
          const required = Math.PI * 2 * (duration / 1000);
          const state: SpinnerState = {
            note,
            obj,
            total: 0,
            lastAngle: null,
            required,
            bonus: 0,
          };
          this.activeSpinners.push(state);
          const tEnd = note.tEnd + this.offsetSec * 1000;
          GameClock.schedule(tEnd / 1000, () => {
            const progress = state.required > 0 ? state.total / state.required : 0;
            this.app.stage.emit('spinner', progress);
            this.fadeOut(state.obj);
            this.activeSpinners = this.activeSpinners.filter((s) => s !== state);
          });
        } else {
          // Hit circle
          this.activeCircles.push({ note, obj });
          GameClock.schedule((tStart + window50()) / 1000, () => {
            const idx = this.activeCircles.findIndex((n) => n.note === note);
            if (idx !== -1) {
              const { obj: o } = this.activeCircles[idx];
              this.fadeOut(o);
              const pos = note.pos
                ? this.toStageCoords(note.pos)
                : { x: (o as any).x ?? 0, y: (o as any).y ?? 0 };
              this.spawnHitEffect(pos.x, pos.y, 'miss');
              const now = GameClock.now();
              const details = {
                reason: 'timeout',
                note: { x: pos.x, y: pos.y, time: tStart },
                actualTime: now,
              };
              this.app.stage.emit('hit', 'miss', details);
              GameClock.schedule((tStart + window50() + 0.2) / 1000, () => {
                const idx2 = this.activeCircles.findIndex((n) => n.note === note);
                if (idx2 !== -1) {
                  this.activeCircles.splice(idx2, 1);
                }
              });
            }
          });
        }
      });
    }
  }

  destroy(): void {
    this.input.destroy();
    this.recordActive = false;
    this.app.stage.off('pointermove', this.onPointerMove);
    this.app.stage.off('pointerdown', this.onPointerDown);
    this.cursor.destroy();
    this.replayCursor?.destroy();
  }

  getRecording(): ReplayFrame[] {
    return this.recording;
  }

  feedInput(frame: ReplayFrame): void {
    this.input.feed(frame.x, frame.y, frame.buttons);
    this.replayCursor?.setTarget(frame.x, frame.y);
  }

  private toStageCoords(pos: { x: number; y: number }): { x: number; y: number } {
    return {
      x: (pos.x / 9) * this.app.screen.width,
      y: (pos.y / 16) * this.app.screen.height,
    };
  }

  private spawnHitEffect(x: number, y: number, judgement: string): void {
    const alphaMap = {
      perfect: 1,
      great: 0.8,
      good: 0.5,
      miss: 0.2,
    } as const;
    const base = alphaMap[judgement as keyof typeof alphaMap];
    if (base === undefined) return;
    const color = parseInt(TOKENS.accent.slice(1), 16);
    const rays = 8;
    const len = 20;
    const burst = new Graphics();
    burst.blendMode = 'add';
    for (let i = 0; i < rays; i++) {
      const ang = (i / rays) * Math.PI * 2;
      burst.moveTo(0, 0);
      burst.lineTo(Math.cos(ang) * len, Math.sin(ang) * len);
    }
    burst.stroke({ width: 2, color, alpha: base });
    burst.position.set(x, y);

    const ring = new Graphics();
    ring.blendMode = 'add';
    ring.circle(0, 0, len).stroke({ width: 2, color, alpha: base });
    ring.position.set(x, y);

    const start = GameClock.now();
    const duration = 120; // ms
    const ticker = () => {
      const age = GameClock.now() - start;
      const t = age / duration;
      const s = 1 + t;
      burst.scale.set(s);
      ring.scale.set(s);
      const alpha = base * Math.max(1 - t, 0);
      burst.alpha = alpha;
      ring.alpha = alpha;
      if (age >= duration) {
        burst.destroy();
        ring.destroy();
        this.app.ticker.remove(ticker);
      }
    };
    this.layers.fx.addChild(ring, burst);
    this.app.ticker.add(ticker);
  }

  private handleInput(): void {
    const x = this.input.x;
    const y = this.input.y;
    const current = GameClock.now() + this.offsetSec * 1000 - getInputOffsetMs();
    let idx = -1;
    for (let i = this.activeCircles.length - 1; i >= 0; i--) {
      const n = this.activeCircles[i];
      const obj = n.obj as any;
      const dx = x - (obj?.x ?? 0);
      const dy = y - (obj?.y ?? 0);
      const dist = Math.sqrt(dx * dx + dy * dy);
      const diffMs = current - n.note.tStart;
      const hitRadius = circleRadius(this.cs) + 5;
      if (dist <= hitRadius) {
        idx = i;
        break;
      }
    }
    if (idx !== -1) {
      const note = this.activeCircles[idx];
      const diffMs = current - note.note.tStart;
      const result = judge(diffMs);
      (note.obj as any).pulse?.();
      const judgementMap = {
        300: 'perfect',
        100: 'great',
        50: 'good',
        Miss: 'miss',
      } as const;
      const judgement = judgementMap[result];
      const pos = note.note.pos
        ? this.toStageCoords(note.note.pos)
        : { x: (note.obj as any).x ?? 0, y: (note.obj as any).y ?? 0 };
      const details = {
        note: { x: pos.x, y: pos.y, time: note.note.tStart },
        input: { x, y, time: current },
        diff: diffMs,
      };
      if (judgement === 'miss') {
        this.fadeOut(note.obj);
        this.activeCircles.splice(idx, 1);
        this.spawnHitEffect(pos.x, pos.y, 'miss');
        (details as any).reason = 'timing';
        this.app.stage.emit('hit', 'miss', details);
        return;
      }
      this.fadeOut(note.obj);
      this.activeCircles.splice(idx, 1);
      this.spawnHitEffect(pos.x, pos.y, judgement);
      this.app.stage.emit('hit', judgement, details);
      return;
    }

    let sIdx = -1;
    let sResult: 300 | 100 | 50 | "Miss" | null = null;
    for (let i = this.activeSliders.length - 1; i >= 0; i--) {
      const s = this.activeSliders[i];
      if (s.startHit) continue;
      const p = s.table.pointAt(0);
      const dist = Math.hypot(x - p.x, y - p.y);
      const diffMs = current - s.note.tStart;
      const result = judge(diffMs);
      const hitRadius = circleRadius(this.cs) + 5;
      if (dist <= hitRadius) {
        sIdx = i;
        sResult = result;
        break;
      }
    }
    if (sIdx !== -1 && sResult !== null) {
      const s = this.activeSliders[sIdx];
      const diffMs = current - s.note.tStart;
      s.startHit = true;
      s.lastHoldTime = current;
      const judgementMap = {
        300: 'perfect',
        100: 'great',
        50: 'good',
        Miss: 'miss',
      } as const;
      const judgement = judgementMap[sResult];
      const pos = this.toStageCoords(s.note.pos ?? { x: 0, y: 0 });
      const details = {
        note: { x: pos.x, y: pos.y, time: s.note.tStart },
        input: { x, y, time: current },
        diff: diffMs,
      };
      this.spawnHitEffect(pos.x, pos.y, judgement);
      this.app.stage.emit('hit', judgement, details);
      return;
    }
    const upcoming = [
      ...this.activeCircles.map((n) => n.note.tStart),
      ...this.activeSliders.filter((s) => !s.startHit).map((s) => s.note.tStart),
      ...this.activeSpinners.map((sp) => sp.note.tStart),
    ];
    const earliest = upcoming.length > 0 ? Math.min(...upcoming) : Infinity;
    if (current >= earliest - window50()) {
      this.spawnHitEffect(this.input.x, this.input.y, 'miss');
      const details = {
        reason: 'no-note',
        input: { x, y, time: current },
      };
      this.app.stage.emit('hit', 'miss', details);
    }
  }

  private handleHold(): void {
    const x = this.input.x;
    const y = this.input.y;
    const now = GameClock.now() - getInputOffsetMs();
    for (const s of this.activeSliders) {
      if (now < s.note.tStart || now > s.tEnd) {
        s.lastHold = false;
        continue;
      }
      const t = now - s.note.tStart;
      const p = s.table.pointAt(t);
      const dist = Math.hypot(x - p.x, y - p.y);
      s.lastHold = dist <= s.followRadius;
      if (s.lastHold) s.lastHoldTime = now;
    }
    for (const sp of this.activeSpinners) {
      const obj = sp.obj as any;
      if (!obj?.position || now < sp.note.tStart || now > sp.note.tEnd) continue;
      const dx = x - obj.x;
      const dy = y - obj.y;
      const angle = Math.atan2(dy, dx);
      if (sp.lastAngle == null) {
        sp.lastAngle = angle;
      } else {
        let delta = angle - sp.lastAngle;
        if (delta > Math.PI) delta -= Math.PI * 2;
        else if (delta < -Math.PI) delta += Math.PI * 2;
        sp.total += Math.abs(delta);
        sp.lastAngle = angle;
        obj.rotation = sp.total;
        const progress = sp.required > 0 ? sp.total / sp.required : 0;
        if (progress >= 1) {
          const bonusSpins = Math.floor(progress) - 1;
          if (bonusSpins > sp.bonus) {
            for (let i = 0; i < bonusSpins - sp.bonus; i++) {
              this.app.stage.emit('hit', 'spinBonus');
            }
            sp.bonus = bonusSpins;
          }
        }
      }
    }
  }

  private fadeOut(obj: DisplayObject, duration = 150): void {
    this.hoveredCircles.delete(obj);
    const start = GameClock.now();
    const ticker = () => {
      const progress = (GameClock.now() - start) / duration;
      obj.alpha = 1 - progress;
      if (progress >= 1) {
        this.app.ticker.remove(ticker);
        obj.destroy();
      }
    };
    this.app.ticker.add(ticker);
  }
}
